import { Card, Title, BadgeDelta } from "@tremor/react";
import { Box, Tooltip, Divider } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import React from "react";
import { dateFormat } from "../../../utils/date";
// Mapeia chaves técnicas para rótulos amigáveis
const LABELS = {
  tempoConfirmacao: 'T. M. confirmação',
  tempoProducao: 'T. M. produção',
  tempoEntrega: 'T. M. entrega',
  tempoTotal: 'T. M. pedido',
};

const LABELS_PEDIDOS = {
  tempoConfirmacao: 'Tempo de confirmação',
  tempoProducao: 'Tempo de produção',
  tempoEntrega: 'Tempo de entrega',
  tempoTotal: 'Tempo total do pedido',
};

const TOOLTIP_MAP = {
  tempoConfirmacao: 'Tempo entre a data do pedido e data de confirmação',
  tempoProducao: 'Tempo entre a data de confirmação até a data de retirada ou saída para entrega',
  tempoEntrega: 'Tempo entre a saída e entrega',
  tempoTotal: 'Tempo total do ciclo do pedido',
};

const defaultFormatter = (v, unit) => {
  if (v == null) return '—';
  if (typeof v === 'string') return v;
  const num = Number(v);
  if (Number.isNaN(num)) return String(v);
  return `${Math.round(num)}${unit ? ` ${unit}` : ''}`;
};

function PedidosMesAtualList({ pedidos }) {
  // ---- estados ----
  const [sortKey, setSortKey] = React.useState("tempoTotal");
  const [sortDir, setSortDir] = React.useState("asc"); // 'asc' | 'desc'
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25); // 10 | 25 | 50 | 100

  // ---- colunas ----
  const columns = [
    { key: "idPedido", label: "ID Pedido", align: "left" },
    { key: "dataPedido", label: "Data", align: "center" },
    { key: "tempoConfirmacao", label: LABELS_PEDIDOS.tempoConfirmacao, align: "right" },
    { key: "tempoProducao", label: LABELS_PEDIDOS.tempoProducao, align: "right" },
    { key: "tempoEntrega", label: LABELS_PEDIDOS.tempoEntrega, align: "right" },
    { key: "tempoTotal", label: LABELS_PEDIDOS.tempoTotal, align: "right" },
  ];

  const alignToClass = (a) =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  // ---- ordenação ----
  const sortIcon = (key) => (key !== sortKey ? "↕" : sortDir === "asc" ? "↑" : "↓");

  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1); // volta pra primeira página ao mudar ordenação
  };

  const sortRows = (rows) => {
    if (!Array.isArray(rows)) return [];
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = a?.[sortKey];
      const vb = b?.[sortKey];
      const na = Number(va);
      const nb = Number(vb);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return String(va).localeCompare(String(vb), "pt-BR", { numeric: true });
    });
    if (sortDir === "desc") arr.reverse();
    return arr;
  };

  const sorted = sortRows(pedidos);

  // ---- paginação ----
  const total = Array.isArray(sorted) ? sorted.length : 0;
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const currentPage = Math.min(page, pageCount);
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, total);
  const pageRows = sorted.slice(startIdx, endIdx);

  // ---- render condicional simples ----
  if (!Array.isArray(pedidos) || pedidos.length === 0) {
    return <p className="px-2 sm:px-4 lg:px-6">Nenhum pedido no mês atual.</p>;
  }

  return (
    <div
      className="overflow-x-auto w-full px-2 sm:px-4 lg:px-6"
      onClick={(e) => e.stopPropagation()}
      role="region"
      aria-labelledby="tabela-pedidos-mes-atual"
    >
      {/* Top bar: tamanho + contador */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-2">
        <div className="text-sm text-gray-700 dark:text-gray-300">
          {/*<strong>{total}</strong> pedidos no mês atual · mostrando{" "}*/}
          pedidos no mês atual · mostrando{" "}
          <strong>{pageRows.length}</strong> (pág. <strong>{currentPage}</strong> de{" "}
          <strong>{pageCount}</strong>)
        </div>

        <label className="flex items-center gap-2 text-sm">
          Tamanho:
          <select
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
        <caption id="tabela-pedidos-mes-atual" className="sr-only">
          Pedidos do mês atual
        </caption>

        <thead className="bg-gray-100 dark:bg-gray-900 sticky top-0 z-10">
          <tr>
            {columns.map((col) => {
              const ariaSort =
                col.key !== sortKey ? "none" : sortDir === "asc" ? "ascending" : "descending";
              return (
                <th
                  key={col.key}
                  aria-sort={ariaSort}
                  className={`px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 ${alignToClass(
                    col.align
                  )}`}
                >
                  <button
                    type="button"
                    onClick={() => onSort(col.key)}
                    className="inline-flex items-center gap-1 hover:underline focus:outline-none"
                    aria-label={`Ordenar por ${col.label}`}
                  >
                    <span>{col.label}</span>
                    <span className="text-[10px] opacity-60">{sortIcon(col.key)}</span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {pageRows.map((pedido, index) => (
            <tr key={pedido.idPedido ?? index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-4 py-2 text-sm text-gray-900 dark:text-gray-100 ${alignToClass(
                    col.align
                  )}`}
                  style={{
                    fontVariantNumeric: col.key !== "idPedido" ? "tabular-nums" : undefined,
                    whiteSpace: "nowrap",
                  }}
                >
                  {col.key === "idPedido" ? (
                    <a
                      href={`https://pdv.sischef.com/impressao-online.html?ids=${pedido[col.key]}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                      title="Imprimir pedido"
                    >
                      {pedido[col.key]}
                    </a>
                  ) 
                  : col.key === "dataPedido" 
                    ? (pedido.dataPedido ? dateFormat(pedido.dataPedido, { pattern: 'dd/MM/yyyy HH:mm:ss' }) : "—") : (defaultFormatter(pedido[col.key]))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pager inferior */}
      <div className="flex items-center justify-between mt-3 text-sm">
        <div className="text-gray-600 dark:text-gray-400">
          {total === 0 ? "0" : `${startIdx + 1}–${endIdx}`} de {total}
        </div>
        <div className="flex items-center gap-1">
          <button
            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 disabled:opacity-40"
            onClick={() => setPage(1)}
            disabled={currentPage === 1}
            aria-label="Primeira página"
          >
            «
          </button>
          <button
            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 disabled:opacity-40"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label="Página anterior"
          >
            ‹
          </button>
          <span className="px-2">
            {currentPage} / {pageCount}
          </span>
          <button
            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 disabled:opacity-40"
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={currentPage === pageCount}
            aria-label="Próxima página"
          >
            ›
          </button>
          <button
            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 disabled:opacity-40"
            onClick={() => setPage(pageCount)}
            disabled={currentPage === pageCount}
            aria-label="Última página"
          >
            »
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TimeByUnitCards({ units, expanded, onToggleExpand }) {
  const orderedKeys = ['tempoConfirmacao', 'tempoProducao', 'tempoEntrega', 'tempoTotal'];

  return (
    <Card className="mt-8">
      <Box>
        {units.map((item, idx) => {
          const unidade = item.unidade || {};
          const tempos = unidade.tempos || {};
          const isExpanded = expanded[unidade.idUnidade];

          return (
            <React.Fragment key={unidade.idUnidade}>
              {/* Título da unidade e seta de expandir */}
              <Box
                className={`flex items-center gap-2 px-2 py-3 cursor-pointer transition ${isExpanded ? 'opacity-100' : 'opacity-80 hover:opacity-100'}`}
                onClick={() => onToggleExpand(unidade.idUnidade)}
                tabIndex={0}
                role="button"
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") onToggleExpand(unidade.idUnidade);
                }}
              >
                <span>
                  {isExpanded ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </span>
                <Title className="text-base">{unidade.unidadeName}</Title>
              </Box>

              {/* Cards */}
              <Box className="px-2 pb-4">
                <Box className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
                  {orderedKeys.map((key) => {
                    const tempo = tempos[key] || {};
                    const mesAtual = tempo.mesAtual || {};
                    const mesAnterior = tempo.mesAnterior || {};
                    const delta = tempo.delta;
                    let deltaType;

                    if (typeof delta === 'number') {
                      deltaType = delta > 0 ? 'decrease' : delta < 0 ? 'increase' : 'unchanged';
                    }

                    return (
                      <Card key={key}>
                        <Box className="flex items-center gap-2 place-content-between">
                          {TOOLTIP_MAP[key] ? (
                            <Tooltip title={TOOLTIP_MAP[key]}>
                              <Title>{LABELS[key] || key}</Title>
                            </Tooltip>
                          ) : (
                            <Title>{LABELS[key] || key}</Title>
                          )}
                          {typeof delta === 'number' && (
                            <BadgeDelta deltaType={deltaType}>
                              {`${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`}
                            </BadgeDelta>
                          )}
                        </Box>
                        <Box component="dd" className="mt-2 flex flex-row justify-between gap-4 w-full">
                          <Box>
                            <p className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                              Mês atual
                            </p>
                            <p className="mt-0 text-tremor-default font-semibold text-tremor-content-strong dark:text-dark-tremor-content-strong">
                              {defaultFormatter(mesAtual.media)}
                            </p>
                          </Box>
                          <Box>
                            <p className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                              Mês ant.
                            </p>
                            <p className="mt-0 text-tremor-default font-semibold text-tremor-content-strong dark:text-dark-tremor-content-strong">
                              {defaultFormatter(mesAnterior.media)}
                            </p>
                          </Box>
                        </Box>
                      </Card>
                    );
                  })}
                </Box>
              </Box>

              {/* Lista expandida */}
              {isExpanded && (
                <Box className="py-2 px-2">
                  <PedidosMesAtualList pedidos={unidade.pedidosMesAtual} />
                </Box>
              )}

              {idx < units.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </Box>
    </Card>
  );
}